import { supabase } from '@/integrations/supabase/client';
import { Project, ProjectQueryOptions } from './workbook-types';
import { mapDbProjectToProject, mapProjectToDbProject } from './mappers';

/**
 * Fetches workbooks for the current user with server-side filtering and pagination.
 * SCALABLE: Filters by both owned workbooks AND editor-accessible workbooks upfront
 */
export async function getWorkbooks(
  options: ProjectQueryOptions = {},
  page: number = 1,
  pageSize: number = 20 // Default page size
): Promise<{ workbooks: Project[]; totalCount: number }> {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError || !sessionData.session) {
    console.error('Error fetching session or no active session:', sessionError);
    throw new Error('User must be authenticated to fetch workbooks.');
  }
  const userId = sessionData.session.user.id;

  // STEP 1: Get workbook IDs where user is an editor (first query)
  const { data: editorWorkbooks, error: editorWorkbookError } = await supabase
    .from('workbook_editors')
    .select('workbook_id')
    .eq('editor_id', userId);

  if (editorWorkbookError) {
    console.error('Failed to fetch editor workbooks:', editorWorkbookError);
    // Continue with owned workbooks only as fallback
  }

  // STEP 2: Build array of accessible workbook IDs (owned + editor)
  const editorWorkbookIds = editorWorkbooks?.map(item => item.workbook_id) || [];
  console.log(`[getWorkbooks] Found ${editorWorkbookIds.length} editor-accessible workbooks for user ${userId}`);

  // STEP 3: Get workbooks with efficient filtering (main query)
  // Include both owned workbooks AND editor-accessible workbooks
  let query = supabase
    .from('workbook')
    .select('*', { count: 'exact' });

  // Core access filter: owned OR editor-accessible
  if (editorWorkbookIds.length > 0) {
    query = query.or(`user_id.eq.${userId},id.in.(${editorWorkbookIds.join(',')})`);
  } else {
    // Fallback: only owned workbooks if no editor access found
    query = query.eq('user_id', userId);
  }

  // Apply status filtering
  if (options.filterByStatus) {
    switch (options.filterByStatus) {
      case 'all': // Non-archived workbooks
        query = query.eq('is_archived', false);
        break;
      case 'archived':
        query = query.eq('is_archived', true);
        break;
    }
  }

  // Apply search query filtering (on title)
  if (options.searchQuery && options.searchQuery.trim() !== '') {
    query = query.ilike('title', `%${options.searchQuery.trim()}%`);
  }

  // Apply tag filtering - Fixed: Execute the subquery first
  if (options.filterByTag) {
    // First, get the workbook IDs that have the specified tag
    const { data: taggedWorkbooks, error: tagError } = await supabase
      .from('workbook_tag_assignments')
      .select('workbook_id')
      .eq('tag_id', options.filterByTag);
    
    if (tagError) {
      console.error('Error fetching tagged workbooks:', tagError);
      throw tagError;
    }
    
    // Extract the workbook IDs and apply to the main query
    const workbookIds = taggedWorkbooks?.map(item => item.workbook_id) || [];
    if (workbookIds.length > 0) {
      query = query.in('id', workbookIds);
    } else {
      // If no workbooks have this tag, return empty result
      return { workbooks: [], totalCount: 0 };
    }
  }

  // Simple ordering by display_order (for DND) and updated_at as fallback
  query = query.order('display_order', { ascending: true, nullsFirst: false })
               .order('updated_at', { ascending: false });

  // Apply pagination
  const start = (page - 1) * pageSize;
  const end = start + pageSize - 1;
  query = query.range(start, end);

  const { data, error, count } = await query;

  if (error) {
    console.error('Error fetching workbooks:', error);
    throw error;
  }

  console.log(`[getWorkbooks] Retrieved ${data?.length || 0} workbooks (${count} total) for user ${userId}`);

  // Get all workbook IDs to fetch associated goals
  const workbookIds = data?.map(w => w.id) || [];
  
  // Fetch associated goals for these workbooks
  let goalData: Record<string, string> = {};
  
  if (workbookIds.length > 0) {
    const { data: junctionData, error: junctionError } = await supabase
      .from('planner_goals_workbooks')
      .select(`
        workbook_id,
        planner_goals (
          id,
          title
        )
      `)
      .in('workbook_id', workbookIds);
    
    if (junctionError) {
      console.error('Error fetching associated goals:', junctionError);
    } else if (junctionData) {
      // Create a map of workbook_id to goal title
      junctionData.forEach(item => {
        if (item.planner_goals) {
          goalData[item.workbook_id] = item.planner_goals.title;
        }
      });
    }
  }

  // Map workbooks and add goal titles
  const mappedWorkbooks = (data || []).map(dbWorkbook => {
    const workbook = mapDbProjectToProject(dbWorkbook);
    // Add goal title if available
    if (goalData[workbook.id]) {
      (workbook as Project & { goalTitle?: string }).goalTitle = goalData[workbook.id];
    }
    return workbook;
  });

  return {
    workbooks: mappedWorkbooks,
    totalCount: count || 0,
  };
}

/**
 * Fetches a single workbook by its ID
 */
export async function getWorkbookById(id: string): Promise<Project | null> {
  const { data, error } = await supabase
    .from('workbook')
    .select('*')
    .eq('id', id)
    .single();
  
  if (error) {
    if (error.code === 'PGRST116') {
      return null; // No rows found
    }
    console.error('Error fetching workbook by ID:', error);
    throw error;
  }
  
  // No need to update last accessed time since we're removing that feature
  return mapDbProjectToProject(data);
}

/**
 * Creates a new workbook
 */
export async function createWorkbook(workbook: Partial<Project>): Promise<Project> {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData.session?.user.id;
    if (!userId) throw new Error('User must be authenticated to create a workbook');

    const dbWorkbook = mapProjectToDbProject(workbook);

    dbWorkbook.user_id = userId;

    // Inject potential new fields if present
    if ("type" in workbook) dbWorkbook.type = workbook.type;

    const { data, error } = await supabase
      .from('workbook')
      .insert(dbWorkbook)
      .select()
      .single();

    if (error) {
      console.error('Error creating workbook:', error);
      throw error;
    }

    return mapDbProjectToProject(data);
  } catch (error) {
    console.error('Error creating workbook:', error);
    throw error;
  }
}

/**
 * Updates an existing workbook
 */
export async function updateWorkbook(id: string, updates: Partial<Project>): Promise<Project> {
  // Convert from Project model to DB model
  const dbUpdates = mapProjectToDbProject(updates);
  
  // Ensure mutual exclusivity between archived and favorite
  if (dbUpdates.is_archived === true) {
    // If archiving, automatically unfavorite
    dbUpdates.is_favorite = false;
  }
  
  // Always update the timestamp when a workbook is updated
  dbUpdates.updated_at = new Date().toISOString();
  
  const { data, error } = await supabase
    .from('workbook')
    .update(dbUpdates)
    .eq('id', id)
    .select()
    .single();
  
  if (error) {
    console.error('Error updating workbook:', error);
    throw error;
  }
  
  return mapDbProjectToProject(data);
}

/**
 * Deletes a workbook permanently
 */
export async function deleteWorkbook(id: string): Promise<void> {
  // Validate that id is a valid UUID format
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  if (!uuidRegex.test(id)) {
    throw new Error(`Invalid UUID format: ${id}`);
  }

  const { error } = await supabase
    .from('workbook')
    .delete()
    .eq('id', id);
  
  if (error) {
    console.error('Error deleting workbook:', error);
    throw error;
  }
}

/**
 * Toggles a workbook's favorite status
 */
export async function toggleWorkbookFavorite(id: string, isFavorite: boolean): Promise<Project> {
  // If we're favoriting the workbook, make sure it's not archived
  if (isFavorite) {
    // First, check if the workbook is archived
    const { data: workbook } = await supabase
      .from('workbook')
      .select('is_archived')
      .eq('id', id)
      .single();
    
    // If workbook is archived, we cannot favorite it
    if (workbook?.is_archived) {
      throw new Error('Cannot favorite an archived workbook');
    }
  }
  
  return await updateWorkbook(id, { isFavorite });
}

/**
 * Toggles a workbook's archived status
 */
export async function toggleWorkbookArchive(id: string, isArchived: boolean): Promise<Project> {
  // If archiving, always unfavorite regardless of current favorite status
  const updates: Partial<Project> = { isArchived };
  
  if (isArchived) {
    updates.isFavorite = false;
  }
  
  return await updateWorkbook(id, updates);
}

// Helper function to get counts based on various status filters
async function getCountForStatus(userId: string, statusFilter: { is_archived?: boolean; is_favorite?: boolean }): Promise<number> {
  let query = supabase
    .from('workbook')
    .select('id', { count: 'exact', head: true }); // head: true makes it very lightweight

  if (typeof statusFilter.is_archived !== 'undefined') {
    query = query.eq('is_archived', statusFilter.is_archived);
  }
  if (typeof statusFilter.is_favorite !== 'undefined') {
    // Ensure favorite implies not archived for this specific count if not explicitly handled by is_archived
    if (statusFilter.is_favorite && typeof statusFilter.is_archived === 'undefined') {
        query = query.eq('is_archived', false); 
    }
    query = query.eq('is_favorite', statusFilter.is_favorite);
  }

  const { error, count } = await query;

  if (error) {
    console.error(`Error fetching count for status ${JSON.stringify(statusFilter)}:`, error);
    // Return 0 or throw error based on desired error handling for counts
    return 0; 
  }
  return count || 0;
}

/**
 * Fetches the count of all non-archived workbooks for the current user.
 */
export async function getAllWorkbooksCount(userId: string): Promise<number> {
  return getCountForStatus(userId, { is_archived: false });
}

/**
 * Fetches the count of favorited (and non-archived) workbooks for the current user.
 */
export async function getFavoritedWorkbooksCount(userId: string): Promise<number> {
  return getCountForStatus(userId, { is_favorite: true, is_archived: false });
}

/**
 * Fetches the count of archived workbooks for the current user.
 */
export async function getArchivedWorkbooksCount(userId: string): Promise<number> {
  return getCountForStatus(userId, { is_archived: true });
}

/**
 * Fetches the total count of all workbooks for the user, regardless of status.
 */
export async function getTotalUserWorkbooksCount(userId: string): Promise<number> {
  // No specific status filter, RLS policies handle access control
  const { error, count } = await supabase
    .from('workbook')
    .select('id', { count: 'exact', head: true });
  
  if (error) {
    console.error('Error fetching total user workbooks count:', error);
    return 0;
  }
  return count || 0;
}
